import React, { useState, useRef, useMemo, useEffect, useCallback } from "react";
import {
  View,
  Text,
  TouchableOpacity,
  TextInput,
  KeyboardAvoidingView,
  Keyboard,
  TouchableWithoutFeedback,
  Platform,
  StyleSheet,
  Image,
  Alert,
  ActivityIndicator,
  ScrollView,
} from "react-native";
import * as ImagePicker from "expo-image-picker";
import BottomSheet, {
  BottomSheetView,
  BottomSheetBackdrop,
  BottomSheetScrollView,
} from "@gorhom/bottom-sheet";
import { Ionicons } from "@expo/vector-icons";
import { SafeAreaView, useSafeAreaInsets } from "react-native-safe-area-context";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../convex/_generated/api";
import { useAuthData } from "../../hooks/useAuthData";
import PhotoCarousel from "../../components/CheckIn/PhotoCarousel";
import TagPeopleButton from "../../components/CheckIn/Buttons/TagPeopleButton";
import RatingButton from "../../components/CheckIn/Buttons/RatingButton";
import VisibilityButton from "../../components/CheckIn/Buttons/VisibilityButton";
import SpeedRailzButton from "../../components/CheckIn/Buttons/SpeedRailzButton";
import { VISIBILITY_OPTIONS } from "../../components/CheckIn/Buttons/VisibilityButton";
import {
  CheckInRatingSection,
  CheckInSpeedRailzSection,
} from "../../components/CheckIn/SpeedRailzCheckinRatingSection";
import { SHOW_SPEEDRAILZ_MAP_PROMO_UI } from "../../utils/speedRailzMapUi";

/** Must match convex/moments.ts MAX_MOMENT_PHOTOS */
const MAX_MOMENT_PHOTOS = 12;

/** Front / back thumb borders. */
const BITE_PHOTO_HIGHLIGHT_COLORS = ["#D6C2B2", "#9C6B42"];

/** Pass through child screens so we don't lose edit mode when returning (otherwise createMoment runs). */
function editMomentNavParams(momentId) {
  return momentId ? { momentId, editMoment: true } : {};
}

function CheckInScreen({ route, navigation }) {
  const insets = useSafeAreaInsets();
  /** Survives child screens that navigate back with incomplete params (PhotoManager used to drop momentId). */
  const editMomentIdRef = useRef(null);
  useEffect(() => {
    if (route.params?.momentId) {
      editMomentIdRef.current = route.params.momentId;
    }
  }, [route.params?.momentId]);
  const momentId = route.params?.momentId ?? editMomentIdRef.current ?? null;
  const isEmbeddedWebRecap = !!route.params?.embeddedWebRecap;
  const isEmbeddedSidebarRecap = !!route.params?.embeddedSidebarRecap;
  const onRequireAppDownload = route.params?.onRequireAppDownload;
  const isEditMode = !!momentId;
  const [editRestaurant, setEditRestaurant] = useState(null);
  const restaurant = route.params?.restaurant ?? editRestaurant ?? null;
  const isSpeedRailzRatingEnabled =
    SHOW_SPEEDRAILZ_MAP_PROMO_UI && restaurant?.isSpeedRailzPartner;
  const [completedPhotos, setCompletedPhotos] = useState([]);
  const [comment, setComment] = useState("");
  const [taggedPeople, setTaggedPeople] = useState([]);
  const [rating, setRating] = useState(0);
  const [drinkQuality, setDrinkQuality] = useState(0);
  const [serviceHospitality, setServiceHospitality] = useState(0);
  const [vibeAtmosphere, setVibeAtmosphere] = useState(0);
  const [visibility, setVisibility] = useState("everyone");
  const [showVisibilitySheet, setShowVisibilitySheet] = useState(false);
  const [showTagPeopleSheet, setShowTagPeopleSheet] = useState(false);
  const [showRatingSheet, setShowRatingSheet] = useState(false);
  const [showSpeedRailzSheet, setShowSpeedRailzSheet] = useState(false);
  const [friendSearchQuery, setFriendSearchQuery] = useState("");
  const [debouncedFriendSearch, setDebouncedFriendSearch] = useState("");
  const visibilitySheetRef = useRef(null);
  const tagPeopleSheetRef = useRef(null);
  const ratingSheetRef = useRef(null);
  const speedRailzSheetRef = useRef(null);

  const { authData } = useAuthData();
  const userPrivyDid = authData?.privyDid ?? null;

  const momentForEdit = useQuery(
    api.moments.getMomentForEdit,
    isEditMode && userPrivyDid && momentId
      ? { momentId, privyDid: userPrivyDid }
      : "skip"
  );

  const generateUploadUrl = useMutation(api.images.generateUploadUrl);
  const updateMomentMutation = useMutation(api.moments.updateMoment);
  const createMomentMutation = useMutation(api.moments.createMoment);

  const hydratedMomentIdRef = useRef(null);
  const [isSavingMoment, setIsSavingMoment] = useState(false);

  useEffect(() => {
    hydratedMomentIdRef.current = null;
  }, [momentId]);

  useEffect(() => {
    if (!isEditMode || !momentForEdit?._id) return;
    if (hydratedMomentIdRef.current === momentForEdit._id) return;
    hydratedMomentIdRef.current = momentForEdit._id;
    setEditRestaurant(momentForEdit.restaurant);
    setComment(momentForEdit.caption ?? "");
    setRating(momentForEdit.rating ?? 0);
    setDrinkQuality(momentForEdit.drinkQuality ?? 0);
    setServiceHospitality(momentForEdit.serviceHospitality ?? 0);
    setVibeAtmosphere(momentForEdit.vibeAtmosphere ?? 0);
    setVisibility(
      momentForEdit.visibility === "public"
        ? "everyone"
        : momentForEdit.visibility === "only_me"
          ? "onlyMe"
          : "friends"
    );
    setTaggedPeople(momentForEdit.taggedFriends ?? []);
    const fromList = momentForEdit.photoUrls?.filter(Boolean) ?? [];
    const uris =
      fromList.length > 0
        ? fromList
        : [momentForEdit.frontPhotoUrl, momentForEdit.backPhotoUrl].filter(
            Boolean
          );
    setCompletedPhotos(uris);
  }, [isEditMode, momentForEdit]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedFriendSearch(friendSearchQuery), 300);
    return () => clearTimeout(t);
  }, [friendSearchQuery]);

  const friendSearchResults = useQuery(
    api.friends.searchFriends,
    userPrivyDid != null
      ? { userPrivyDid, searchQuery: debouncedFriendSearch }
      : "skip"
  ) ?? [];

  const visibilitySnapPoints = useMemo(() => ["38%"], []);
  const tagPeopleSnapPoints = useMemo(() => ["55%"], []);
  const ratingSnapPoints = useMemo(() => ["45%"], []);
  const speedRailzSnapPoints = useMemo(() => ["70%"], []);

  const renderVisibilityBackdrop = useCallback(
    (props) => (
      <BottomSheetBackdrop
        {...props}
        appearsOnIndex={0}
        disappearsOnIndex={-1}
        opacity={0.4}
      />
    ),
    []
  );

  const closeVisibilitySheet = () => {
    visibilitySheetRef.current?.close();
  };
  const closeTagPeopleSheet = () => tagPeopleSheetRef.current?.close();
  const closeRatingSheet = () => ratingSheetRef.current?.close();
  const closeSpeedRailzSheet = () => speedRailzSheetRef.current?.close();

  const handleVisibilitySheetClose = () => setShowVisibilitySheet(false);
  const handleTagPeopleSheetClose = () => {
    setShowTagPeopleSheet(false);
    setFriendSearchQuery("");
  };
  const handleRatingSheetClose = () => setShowRatingSheet(false);
  const handleSpeedRailzSheetClose = () => setShowSpeedRailzSheet(false);

  const handleSelectTagPerson = (friend) => {
    if (!taggedPeople.some((f) => f._id === friend._id)) {
      setTaggedPeople((prev) => [...prev, friend]);
    }
  };
  const handleRemoveTagPerson = (friendId) => {
    setTaggedPeople((prev) => prev.filter((f) => f._id !== friendId));
  };

  useEffect(() => {
    if (showVisibilitySheet) visibilitySheetRef.current?.expand();
    else visibilitySheetRef.current?.close();
  }, [showVisibilitySheet]);
  useEffect(() => {
    if (showTagPeopleSheet) tagPeopleSheetRef.current?.expand();
    else tagPeopleSheetRef.current?.close();
  }, [showTagPeopleSheet]);
  useEffect(() => {
    if (showRatingSheet) ratingSheetRef.current?.expand();
    else ratingSheetRef.current?.close();
  }, [showRatingSheet]);
  useEffect(() => {
    if (showSpeedRailzSheet) speedRailzSheetRef.current?.expand();
    else speedRailzSheetRef.current?.close();
  }, [showSpeedRailzSheet]);

  const handlePhotosDone = (photoUris) => {
    setCompletedPhotos((photoUris || []).slice(0, MAX_MOMENT_PHOTOS));
  };

  const showAddPhotoOptions = () => {
    if (isEmbeddedWebRecap) {
      onRequireAppDownload?.();
      return;
    }
    Alert.alert("Add photo", undefined, [
      { text: "Cancel", style: "cancel" },
      {
        text: "Take photo",
        onPress: () =>
          navigation.navigate("CheckInCamera", {
            initialPhotoUris: completedPhotos,
            restaurant,
            originRouteKey: route.key,
            ...editMomentNavParams(momentId),
          }),
      },
      {
        text: "Choose from library",
        onPress: pickFromLibrary,
      },
    ]);
  };

  const pickFromLibrary = async () => {
    try {
      const { status } =
        await ImagePicker.requestMediaLibraryPermissionsAsync();
      if (status !== "granted") {
        Alert.alert(
          "Permission needed",
          "Please allow access to your photo library to add photos."
        );
        return;
      }
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        allowsMultipleSelection: true,
        quality: 0.9,
      });
      if (!result.canceled && result.assets?.length > 0) {
        const newUris = result.assets.map((a) => a.uri);
        setCompletedPhotos((prev) =>
          [...prev, ...newUris].slice(0, MAX_MOMENT_PHOTOS)
        );
      }
    } catch (error) {
      console.error("Error picking from library:", error);
      Alert.alert("Error", "Could not open photo library.");
    }
  };

  useEffect(() => {
    const completed = route.params?.completedPhotoUris;
    if (Array.isArray(completed)) {
      handlePhotosDone(completed);
      navigation?.setParams?.({ completedPhotoUris: undefined });
    }
  }, [route.params?.completedPhotoUris, navigation]);

  const uploadLocalUriToStorage = async (uri) => {
    const postUrl = await generateUploadUrl({ privyDid: userPrivyDid });
    if (!postUrl) throw new Error("Failed to get upload URL");
    const response = await fetch(uri);
    const blob = await response.blob();
    const uploadResponse = await fetch(postUrl, {
      method: "POST",
      body: blob,
      headers: { "Content-Type": "image/jpeg" },
    });
    if (!uploadResponse.ok) throw new Error("Upload failed");
    const { storageId } = await uploadResponse.json();
    return storageId;
  };

  /** Match by URL so indices stay valid if the list was compacted when loading. */
  const resolvePhotoForEdit = async (uri) => {
    if (uri.startsWith("http://") || uri.startsWith("https://")) {
      const urls = momentForEdit?.photoUrls ?? [];
      const ids = momentForEdit?.photoIds ?? [];
      const idx = urls.indexOf(uri);
      if (idx !== -1 && ids[idx]) {
        return ids[idx];
      }
      if (uri === momentForEdit?.frontPhotoUrl && momentForEdit?.frontPhoto) {
        return momentForEdit.frontPhoto;
      }
      if (uri === momentForEdit?.backPhotoUrl && momentForEdit?.backPhoto) {
        return momentForEdit.backPhoto;
      }
    }
    return uploadLocalUriToStorage(uri);
  };

  const handleFinishCheckIn = async () => {
    if (!userPrivyDid) {
      Alert.alert("Sign in", "Please sign in to save your bite.");
      return;
    }
    if (!rating || rating < 1) {
      Alert.alert("Rating", "Please choose a rating.");
      return;
    }
    if (completedPhotos.length > MAX_MOMENT_PHOTOS) {
      Alert.alert(
        "Photos",
        `You can add up to ${MAX_MOMENT_PHOTOS} photos per moment.`
      );
      return;
    }

    if (isEditMode) {
      if (!momentForEdit) return;
      setIsSavingMoment(true);
      try {
        const photoIds = [];
        for (let i = 0; i < completedPhotos.length; i++) {
          photoIds.push(await resolvePhotoForEdit(completedPhotos[i]));
        }

        await updateMomentMutation({
          momentId: momentForEdit._id,
          privyDid: userPrivyDid,
          rating,
          // Send "" so clearing the caption is persisted; `|| undefined` would omit the field.
          caption: comment.trim(),
          visibility:
            visibility === "everyone"
              ? "public"
              : visibility === "onlyMe"
                ? "only_me"
                : "friends",
          taggedFriends: taggedPeople.map((f) => f._id),
          drinkQuality:
            isSpeedRailzRatingEnabled && drinkQuality > 0
              ? drinkQuality
              : undefined,
          serviceHospitality:
            isSpeedRailzRatingEnabled && serviceHospitality > 0
              ? serviceHospitality
              : undefined,
          vibeAtmosphere:
            isSpeedRailzRatingEnabled && vibeAtmosphere > 0
              ? vibeAtmosphere
              : undefined,
          photoIds,
        });
        navigation.navigate("Tabs", { screen: "Social" });
      } catch (e) {
        console.error(e);
        Alert.alert("Error", e?.message || "Could not save moment.");
      } finally {
        setIsSavingMoment(false);
      }
      return;
    }

    if (!restaurant?._id) {
      Alert.alert("Restaurant", "Choose a restaurant before recapping your bite.");
      return;
    }

    setIsSavingMoment(true);
    try {
      const photoIds = [];
      for (let i = 0; i < completedPhotos.length; i++) {
        // fetch() works for file://, content://, and http(s) (e.g. some pickers / web).
        photoIds.push(await uploadLocalUriToStorage(completedPhotos[i]));
      }

      await createMomentMutation({
        privyDid: userPrivyDid,
        restaurantId: restaurant._id,
        rating,
        caption: comment.trim() || undefined,
        visibility:
          visibility === "everyone"
            ? "public"
            : visibility === "onlyMe"
              ? "only_me"
              : "friends",
        taggedFriends: taggedPeople.map((f) => f._id),
        drinkQuality:
          isSpeedRailzRatingEnabled && drinkQuality > 0
            ? drinkQuality
            : undefined,
        serviceHospitality:
          isSpeedRailzRatingEnabled && serviceHospitality > 0
            ? serviceHospitality
            : undefined,
        vibeAtmosphere:
          isSpeedRailzRatingEnabled && vibeAtmosphere > 0
            ? vibeAtmosphere
            : undefined,
        photoIds: photoIds.length > 0 ? photoIds : undefined,
      });
      navigation.goBack();
    } catch (e) {
      console.error(e);
      Alert.alert("Error", e?.message || "Could not save your bite.");
    } finally {
      setIsSavingMoment(false);
    }
  };

  if (isEditMode && momentForEdit === undefined) {
    return (
      <SafeAreaView
        style={[styles.loadingContainer, isEmbeddedSidebarRecap && styles.screenEmbedded]}
      >
        <ActivityIndicator size="large" color="#4d2c1d" />
        <Text style={styles.loadingText}>Loading moment…</Text>
      </SafeAreaView>
    );
  }

  if (isEditMode && momentForEdit === null) {
    return (
      <SafeAreaView
        style={[styles.errorContainer, isEmbeddedSidebarRecap && styles.screenEmbedded]}
      >
        <Text style={styles.errorTitle}>
          Could not load this moment.
        </Text>
        <TouchableOpacity
          style={styles.errorButton}
          onPress={() => navigation.goBack()}
        >
          <Text style={styles.errorButtonText}>Go back</Text>
        </TouchableOpacity>
      </SafeAreaView>
    );
  }

  const mainColumn = (
    <>
      {/* Header: X left, Recap Bite centered */}
      <View style={styles.headerRow}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={{ width: 32 }}>
          <Ionicons name="close" size={28} color="#4d2c1d" />
        </TouchableOpacity>
        <Text style={styles.headerTitle} pointerEvents="none">
          {isEditMode ? "Edit moment" : "Recap Bite"}
        </Text>
        <View style={{ width: 32 }} />
      </View>

      <View style={styles.flexOne}>
        {/* Restaurant being checked into */}
        {restaurant ? (
          <View style={styles.restaurantWrap}>
            <Text style={styles.restaurantName}>
              {restaurant.name}
            </Text>
          </View>
        ) : (
          <Text style={styles.noRestaurantText}>
            No restaurant selected.
          </Text>
        )}

        {/* Photos section: Add photo first, then thumbnails */}
        <View style={styles.photosSection}>
          <Text style={styles.sectionLabel}>Photos</Text>
          <PhotoCarousel
            photos={completedPhotos}
            thumbSize={80}
            highlightIndices={[0, 1]}
            highlightColors={BITE_PHOTO_HIGHLIGHT_COLORS}
            onRemove={(index) =>
              setCompletedPhotos((prev) => prev.filter((_, i) => i !== index))
            }
            onAddPhoto={() =>
              completedPhotos.length > 0 && !isEmbeddedWebRecap
                ? navigation.navigate("PhotoManager", {
                    photoUris: completedPhotos,
                    restaurant,
                    originRouteKey: route.key,
                    ...editMomentNavParams(momentId),
                  })
                : showAddPhotoOptions()
            }
          />
        </View>

        {/* Comment */}
        <View style={styles.section}>
          <Text style={styles.sectionLabel}>Comment</Text>
          <TextInput
            style={styles.commentInput}
            placeholder="Add a comment..."
            placeholderTextColor="#9ca3af"
            value={comment}
            onChangeText={setComment}
            multiline
            maxLength={500}
            numberOfLines={3}
          />
        </View>


        <TagPeopleButton
          taggedPeople={taggedPeople}
          onPress={() => setShowTagPeopleSheet(true)}
        />

        <VisibilityButton
          visibility={visibility}
          onPress={() => setShowVisibilitySheet(true)}
        />

        <RatingButton
          rating={rating}
          onPress={() => setShowRatingSheet(true)}
        />

        {isSpeedRailzRatingEnabled ? (
          <SpeedRailzButton
            drinkQuality={drinkQuality}
            serviceHospitality={serviceHospitality}
            vibeAtmosphere={vibeAtmosphere}
            onPress={() => setShowSpeedRailzSheet(true)}
          />
        ) : null}

        {/* Recap Bite button */}
        <View
          style={[
            styles.finishSection,
            {
              paddingBottom:
                isEmbeddedSidebarRecap ? 24 : 24 + insets.bottom,
            },
          ]}
        >
          <TouchableOpacity
            style={styles.finishButton}
            onPress={handleFinishCheckIn}
            activeOpacity={0.8}
            disabled={isSavingMoment}
          >
            {isSavingMoment ? (
              <>
                <ActivityIndicator color="#fff" size="small" />
                <Text style={styles.finishButtonText}>
                  {isEditMode ? "Saving…" : "Saving bite…"}
                </Text>
              </>
            ) : (
              <>
                <Text style={styles.finishButtonText}>
                  {isEditMode ? "Save" : "Recap Bite"}
                </Text>
                {!isEditMode ? (
                  <Text style={styles.finishButtonDisco}>🪩</Text>
                ) : null}
              </>
            )}
          </TouchableOpacity>
        </View>
      </View>
    </>
  );

  return (
    <SafeAreaView
      style={[styles.screen, isEmbeddedSidebarRecap && styles.screenEmbedded]}
    >
      <KeyboardAvoidingView
        style={styles.flexOne}
        behavior={Platform.OS === "ios" ? "padding" : undefined}
        keyboardVerticalOffset={Platform.OS === "ios" ? 0 : 0}
      >
        <TouchableWithoutFeedback
          onPress={Platform.OS === "web" ? undefined : Keyboard.dismiss}
          disabled={Platform.OS === "web"}
          accessible={false}
        >
        <View
          style={[
            styles.contentContainer,
            isEmbeddedSidebarRecap && styles.contentContainerEmbedded,
          ]}
        >
          {isEmbeddedSidebarRecap ? (
            <ScrollView
              style={styles.embeddedScroll}
              contentContainerStyle={styles.embeddedScrollContent}
              keyboardShouldPersistTaps="handled"
            >
              {mainColumn}
            </ScrollView>
          ) : (
            mainColumn
          )}

          <BottomSheet
            ref={visibilitySheetRef}
            index={-1}
            snapPoints={visibilitySnapPoints}
            enablePanDownToClose
            onClose={handleVisibilitySheetClose}
            backdropComponent={renderVisibilityBackdrop}
            backgroundStyle={styles.visibilitySheetBackground}
            handleIndicatorStyle={styles.visibilitySheetHandleIndicator}
          >
            <BottomSheetView style={styles.visibilitySheetContent}>
              <Text style={styles.visibilitySheetTitle}>Who can see this?</Text>
              {VISIBILITY_OPTIONS.map((opt) => (
                <TouchableOpacity
                  key={opt.value}
                  style={[
                    styles.visibilitySheetOption,
                    visibility === opt.value && styles.visibilitySheetOptionSelected,
                  ]}
                  onPress={() => setVisibility(opt.value)}
                  activeOpacity={0.7}
                >
                  <Text
                    style={[
                      styles.visibilitySheetOptionText,
                      visibility === opt.value && styles.visibilitySheetOptionTextSelected,
                    ]}
                  >
                    {opt.label}
                  </Text>
                  {visibility === opt.value && (
                    <Ionicons name="checkmark" size={22} color="#5E3D2D" />
                  )}
                </TouchableOpacity>
              ))}
              <TouchableOpacity
                onPress={closeVisibilitySheet}
                style={styles.visibilitySheetCloseButton}
                hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
              >
                <Text style={styles.visibilitySheetCloseText}>Close</Text>
              </TouchableOpacity>
            </BottomSheetView>
          </BottomSheet>

          {/* Tag people bottom sheet */}
          <BottomSheet
            ref={tagPeopleSheetRef}
            index={-1}
            snapPoints={tagPeopleSnapPoints}
            enablePanDownToClose
            onClose={handleTagPeopleSheetClose}
            backdropComponent={renderVisibilityBackdrop}
            backgroundStyle={styles.visibilitySheetBackground}
            handleIndicatorStyle={styles.visibilitySheetHandleIndicator}
          >
            <BottomSheetScrollView
              style={styles.tagPeopleSheetScroll}
              contentContainerStyle={styles.tagPeopleSheetContent}
              keyboardShouldPersistTaps="handled"
            >
              <Text style={styles.visibilitySheetTitle}>Tag people</Text>
              {taggedPeople.length > 0 && (
                <View style={styles.tagPeopleChipsRow}>
                  {taggedPeople.map((friend) => (
                    <View key={friend._id} style={styles.tagPeopleChip}>
                      {friend.pfpUrl ? (
                        <Image
                          source={{ uri: friend.pfpUrl }}
                          style={styles.tagPeopleChipAvatar}
                        />
                      ) : (
                        <View style={styles.tagPeopleChipAvatarPlaceholder}>
                          <Ionicons name="person" size={12} color="#4d2c1d" />
                        </View>
                      )}
                      <Text style={styles.tagPeopleChipText} numberOfLines={1}>
                        @{friend.username || friend.name || "friend"}
                      </Text>
                      <TouchableOpacity
                        onPress={() => handleRemoveTagPerson(friend._id)}
                        hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                      >
                        <Ionicons name="close-circle" size={18} color="#4d2c1d" />
                      </TouchableOpacity>
                    </View>
                  ))}
                </View>
              )}
              <TextInput
                style={styles.tagPeopleSearchInput}
                placeholder="Search friends..."
                placeholderTextColor="#9ca3af"
                value={friendSearchQuery}
                onChangeText={setFriendSearchQuery}
              />
              {friendSearchResults.length > 0 ? (
                friendSearchResults.map((friend) => {
                  const isSelected = taggedPeople.some((f) => f._id === friend._id);
                  return (
                    <TouchableOpacity
                      key={friend._id}
                      style={[
                        styles.tagPeopleResultItem,
                        isSelected && styles.tagPeopleResultItemSelected,
                      ]}
                      onPress={() => {
                        if (isSelected) {
                          handleRemoveTagPerson(friend._id);
                        } else {
                          handleSelectTagPerson(friend);
                        }
                      }}
                      activeOpacity={0.7}
                    >
                      {friend.pfpUrl ? (
                        <Image
                          source={{ uri: friend.pfpUrl }}
                          style={styles.tagPeopleResultAvatar}
                        />
                      ) : (
                        <View style={styles.tagPeopleResultAvatarPlaceholder}>
                          <Ionicons name="person" size={20} color="#4d2c1d" />
                        </View>
                      )}
                      <View style={styles.tagPeopleResultInfo}>
                        <Text style={styles.tagPeopleResultName}>
                          {friend.name || friend.username || "Friend"}
                        </Text>
                        {friend.username ? (
                          <Text style={styles.tagPeopleResultUsername}>
                            @{friend.username}
                          </Text>
                        ) : null}
                      </View>
                      {isSelected ? (
                        <Ionicons name="checkmark-circle" size={26} color="#5E3D2D" />
                      ) : (
                        <View style={styles.tagPeopleResultCheckbox} />
                      )}
                    </TouchableOpacity>
                  );
                })
              ) : (
                <View style={styles.tagPeopleEmpty}>
                  <Text style={styles.tagPeopleEmptyText}>
                    {friendSearchQuery.trim() === ""
                      ? "Start typing to search friends..."
                      : "No friends found"}
                  </Text>
                </View>
              )}
              <TouchableOpacity
                onPress={closeTagPeopleSheet}
                style={styles.visibilitySheetCloseButton}
                hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
              >
                <Text style={styles.visibilitySheetCloseText}>Close</Text>
              </TouchableOpacity>
            </BottomSheetScrollView>
          </BottomSheet>

          {/* Rating bottom sheet */}
          <BottomSheet
            ref={ratingSheetRef}
            index={-1}
            snapPoints={ratingSnapPoints}
            enablePanDownToClose
            onClose={handleRatingSheetClose}
            backdropComponent={renderVisibilityBackdrop}
            backgroundStyle={styles.visibilitySheetBackground}
            handleIndicatorStyle={styles.visibilitySheetHandleIndicator}
          >
            <BottomSheetView style={styles.visibilitySheetContent}>
              <Text style={styles.visibilitySheetTitle}>Rating</Text>
              <CheckInRatingSection
                rating={rating}
                onRatingChange={setRating}
              />
              <TouchableOpacity
                onPress={closeRatingSheet}
                style={styles.visibilitySheetCloseButton}
                hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
              >
                <Text style={styles.visibilitySheetCloseText}>Close</Text>
              </TouchableOpacity>
            </BottomSheetView>
          </BottomSheet>

          {/* SpeedRailz bottom sheet */}
          {isSpeedRailzRatingEnabled ? (
            <BottomSheet
              ref={speedRailzSheetRef}
              index={-1}
              snapPoints={speedRailzSnapPoints}
              enablePanDownToClose
              onClose={handleSpeedRailzSheetClose}
              backdropComponent={renderVisibilityBackdrop}
              backgroundStyle={styles.visibilitySheetBackground}
              handleIndicatorStyle={styles.visibilitySheetHandleIndicator}
            >
              <BottomSheetScrollView
                style={styles.tagPeopleSheetScroll}
                contentContainerStyle={styles.tagPeopleSheetContent}
                keyboardShouldPersistTaps="handled"
              >
                <Text style={styles.visibilitySheetTitle}>SpeedRailz</Text>
                <CheckInSpeedRailzSection
                  isSpeedRailzPartner={isSpeedRailzRatingEnabled}
                  drinkQuality={drinkQuality}
                  serviceHospitality={serviceHospitality}
                  vibeAtmosphere={vibeAtmosphere}
                  onDrinkQualityChange={setDrinkQuality}
                  onServiceHospitalityChange={setServiceHospitality}
                  onVibeAtmosphereChange={setVibeAtmosphere}
                />
                <TouchableOpacity
                  onPress={closeSpeedRailzSheet}
                  style={styles.visibilitySheetCloseButton}
                  hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
                >
                  <Text style={styles.visibilitySheetCloseText}>Close</Text>
                </TouchableOpacity>
              </BottomSheetScrollView>
            </BottomSheet>
          ) : null}
        </View>
        </TouchableWithoutFeedback>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}

const styles = {
  screen: {
    flex: 1,
    backgroundColor: "#F9F5F3",
  },
  screenEmbedded: {
    minHeight: 0,
  },
  flexOne: {
    flex: 1,
  },
  contentContainer: {
    flex: 1,
    paddingHorizontal: 24,
  },
  contentContainerEmbedded: {
    minHeight: 0,
  },
  embeddedScroll: {
    flex: 1,
  },
  embeddedScrollContent: {
    flexGrow: 1,
    paddingBottom: 8,
  },
  loadingContainer: {
    flex: 1,
    backgroundColor: "#F9F5F3",
    alignItems: "center",
    justifyContent: "center",
  },
  loadingText: {
    marginTop: 16,
    color: "#4d2c1d",
    fontFamily: "AbhayaLibre_400Regular, serif",
  },
  errorContainer: {
    flex: 1,
    backgroundColor: "#F9F5F3",
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 32,
  },
  errorTitle: {
    fontSize: 18,
    fontFamily: "AbhayaLibre_700Bold, serif",
    color: "#4d2c1d",
    textAlign: "center",
  },
  errorButton: {
    marginTop: 24,
    backgroundColor: "#4d2c1d",
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 12,
  },
  errorButtonText: {
    color: "#FFFFFF",
    fontFamily: "AbhayaLibre_700Bold, serif",
  },
  headerRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: 16,
  },
  headerTitle: {
    position: "absolute",
    left: 0,
    right: 0,
    textAlign: "center",
    fontSize: 30,
    fontFamily: "AbhayaLibre_700Bold, serif",
    color: "#4d2c1d",
  },
  restaurantWrap: {
    marginTop: 16,
    alignItems: "center",
  },
  restaurantName: {
    fontSize: 24,
    fontFamily: "AbhayaLibre_700Bold, serif",
    color: "#4d2c1d",
    textAlign: "center",
  },
  noRestaurantText: {
    marginTop: 16,
    fontSize: 18,
    fontFamily: "AbhayaLibre_400Regular, serif",
    color: "#4d2c1d",
    textAlign: "center",
  },
  photosSection: {
    marginTop: 24,
  },
  section: {
    marginTop: 24,
  },
  sectionLabel: {
    fontSize: 15,
    fontWeight: "600",
    color: "#4d2c1d",
    marginBottom: 8,
  },
  commentInput: {
    backgroundColor: "#fff",
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 12,
    fontSize: 16,
    color: "#1f2937",
    minHeight: 88,
    textAlignVertical: "top",
  },
  visibilitySheetBackground: {
    backgroundColor: "#fff",
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
  },
  visibilitySheetHandleIndicator: {
    backgroundColor: "#d1d5db",
    width: 36,
  },
  visibilitySheetContent: {
    paddingHorizontal: 24,
    paddingBottom: 34,
  },
  visibilitySheetTitle: {
    fontSize: 18,
    fontWeight: "600",
    color: "#4d2c1d",
    marginBottom: 16,
    textAlign: "center",
  },
  visibilitySheetCloseButton: {
    alignSelf: "center",
    backgroundColor: "#e8e0d9",
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 12,
    marginTop: 24,
  },
  visibilitySheetCloseText: {
    fontSize: 16,
    fontWeight: "600",
    color: "#4d2c1d",
  },
  visibilitySheetOption: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: 16,
    paddingHorizontal: 4,
    borderBottomWidth: 1,
    borderBottomColor: "#f3f4f6",
  },
  visibilitySheetOptionSelected: {
    backgroundColor: "#F9F5F3",
    marginHorizontal: -24,
    paddingHorizontal: 28,
  },
  visibilitySheetOptionText: {
    fontSize: 16,
    color: "#374151",
  },
  visibilitySheetOptionTextSelected: {
    color: "#5E3D2D",
    fontWeight: "600",
  },
  tagPeopleSheetScroll: {
    flex: 1,
  },
  tagPeopleSheetContent: {
    paddingHorizontal: 24,
    paddingBottom: 34,
  },
  tagPeopleChipsRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
    marginBottom: 12,
  },
  tagPeopleChip: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#F9F5F3",
    paddingVertical: 6,
    paddingLeft: 8,
    paddingRight: 4,
    borderRadius: 20,
    gap: 6,
    maxWidth: "100%",
  },
  tagPeopleChipAvatar: {
    width: 20,
    height: 20,
    borderRadius: 10,
  },
  tagPeopleChipAvatarPlaceholder: {
    width: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: "#e5e7eb",
    alignItems: "center",
    justifyContent: "center",
  },
  tagPeopleChipText: {
    fontSize: 14,
    color: "#4d2c1d",
    maxWidth: 100,
  },
  tagPeopleSearchInput: {
    backgroundColor: "#fff",
    borderRadius: 12,
    paddingHorizontal: 14,
    paddingVertical: 12,
    fontSize: 16,
    color: "#1f2937",
    borderWidth: 1,
    borderColor: "#e5e7eb",
    marginBottom: 12,
  },
  tagPeopleResultItem: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 12,
    paddingHorizontal: 4,
    borderBottomWidth: 1,
    borderBottomColor: "#f3f4f6",
  },
  tagPeopleResultItemSelected: {
    backgroundColor: "#F9F5F3",
    marginHorizontal: -24,
    paddingHorizontal: 28,
  },
  tagPeopleResultAvatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    marginRight: 12,
  },
  tagPeopleResultAvatarPlaceholder: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: "#e5e7eb",
    alignItems: "center",
    justifyContent: "center",
    marginRight: 12,
  },
  tagPeopleResultInfo: {
    flex: 1,
  },
  tagPeopleResultName: {
    fontSize: 16,
    color: "#374151",
    fontWeight: "500",
  },
  tagPeopleResultUsername: {
    fontSize: 14,
    color: "#6b7280",
    marginTop: 2,
  },
  tagPeopleResultCheckbox: {
    width: 24,
    height: 24,
    borderRadius: 12,
    borderWidth: 2,
    borderColor: "#d1d5db",
  },
  tagPeopleEmpty: {
    paddingVertical: 24,
    alignItems: "center",
  },
  tagPeopleEmptyText: {
    fontSize: 15,
    color: "#6b7280",
  },
  finishSection: {
    marginTop: 20,
    paddingTop: 12,
  },
  finishButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#4d2c1d",
    borderRadius: 12,
    paddingVertical: 16,
    gap: 8,
  },
  finishButtonText: {
    fontSize: 18,
    fontWeight: "600",
    color: "#fff",
  },
  finishButtonDisco: {
    fontSize: 24,
    lineHeight: 28,
  },
};

export default CheckInScreen;
